// src/components/attestations/AttestationDetails.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
    fetchAttestation,
    deleteAttestation,
    updateAttestationStatus,
    syncAttestation,
    clearAttestationError,
} from '../../store/actions/attestationActions';
import LoadingSpinner from '../common/LoadingSpinner';
import Alert from '../common/Alert';
import ConfirmDialog from '../common/ConfirmDialog';
import Tabs from '../common/Tabs';

const AttestationDetails = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const {
        selectedAttestation,
        loading,
        error,
        syncProgress
    } = useSelector((state) => state.attestations);

    const [activeTab, setActiveTab] = useState('info');
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [newStatus, setNewStatus] = useState('');
    const [showDocumentManager, setShowDocumentManager] = useState(false);

    const statusOptions = [
        { value: 'ACTIVE', label: 'Actif', color: '#10b981', bg: '#d1fae5' },
        { value: 'PENDING', label: 'En attente', color: '#f59e0b', bg: '#fef3c7' },
        { value: 'CANCELLED', label: 'Annulé', color: '#ef4444', bg: '#fee2e2' },
        { value: 'EXPIRED', label: 'Expiré', color: '#6b7280', bg: '#f3f4f6' },
        { value: 'ARCHIVED', label: 'Archivé', color: '#9ca3af', bg: '#e5e7eb' },
    ];

    // Styles object
    const styles = {
        container: {
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '2rem 1rem',
        },
        header: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: '2rem',
        },
        headerLeft: {
            display: 'flex',
            alignItems: 'center',
            gap: '1rem',
        },
        backButton: {
            padding: '0.5rem',
            backgroundColor: '#f3f4f6',
            border: 'none',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            color: '#374151',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: 'background-color 0.2s',
        },
        backButtonHover: {
            backgroundColor: '#e5e7eb',
        },
        title: {
            fontSize: '1.5rem',
            fontWeight: 700,
            color: '#111827',
            margin: 0,
        },
        headerActions: {
            display: 'flex',
            gap: '0.75rem',
        },
        actionButton: {
            padding: '0.5rem 1rem',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            transition: 'all 0.2s',
        },
        editButton: {
            backgroundColor: '#4f46e5',
            color: '#ffffff',
        },
        editButtonHover: {
            backgroundColor: '#4338ca',
        },
        syncButton: {
            backgroundColor: '#8b5cf6',
            color: '#ffffff',
        },
        syncButtonHover: {
            backgroundColor: '#7c3aed',
        },
        statusButton: {
            backgroundColor: '#f59e0b',
            color: '#ffffff',
        },
        statusButtonHover: {
            backgroundColor: '#d97706',
        },
        deleteButton: {
            backgroundColor: '#dc2626',
            color: '#ffffff',
        },
        deleteButtonHover: {
            backgroundColor: '#b91c1c',
        },
        icon: {
            width: '1.25rem',
            height: '1.25rem',
        },
        content: {
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            boxShadow: '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06)',
            overflow: 'hidden',
        },
        hero: {
            padding: '2rem',
            backgroundColor: '#f9fafb',
            borderBottom: '1px solid #e5e7eb',
        },
        heroContent: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
        },
        heroLeft: {
            flex: 1,
        },
        attestationNumber: {
            fontSize: '2rem',
            fontWeight: 700,
            color: '#111827',
            marginBottom: '0.5rem',
        },
        attestationMeta: {
            display: 'flex',
            gap: '2rem',
            marginTop: '1rem',
        },
        metaItem: {
            display: 'flex',
            flexDirection: 'column',
        },
        metaLabel: {
            fontSize: '0.75rem',
            textTransform: 'uppercase',
            color: '#6b7280',
            marginBottom: '0.25rem',
        },
        metaValue: {
            fontSize: '1rem',
            fontWeight: 500,
            color: '#111827',
        },
        badge: {
            display: 'inline-flex',
            padding: '0.5rem 1rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            borderRadius: '9999px',
        },
        syncBadge: {
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.5rem',
            padding: '0.5rem 1rem',
            backgroundColor: '#f3f4f6',
            borderRadius: '0.5rem',
            fontSize: '0.875rem',
        },
        syncSuccess: {
            color: '#10b981',
        },
        progressBar: {
            marginTop: '0.5rem',
            height: '0.5rem',
            backgroundColor: '#e5e7eb',
            borderRadius: '9999px',
            overflow: 'hidden',
            width: '200px',
        },
        progressFill: {
            height: '100%',
            backgroundColor: '#8b5cf6',
            transition: 'width 0.3s ease',
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
            gap: '1.5rem',
            marginTop: '2rem',
        },
        statCard: {
            backgroundColor: '#ffffff',
            padding: '1rem',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
        },
        statLabel: {
            fontSize: '0.875rem',
            color: '#6b7280',
            marginBottom: '0.5rem',
        },
        statValue: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#111827',
        },
        tabContent: {
            padding: '2rem',
        },
        infoGrid: {
            display: 'grid',
            gridTemplateColumns: 'repeat(2, 1fr)',
            gap: '2rem',
        },
        infoSection: {
            marginBottom: '2rem',
        },
        sectionTitle: {
            fontSize: '1.125rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '1.5rem',
            paddingBottom: '0.5rem',
            borderBottom: '2px solid #f3f4f6',
        },
        infoList: {
            listStyle: 'none',
            padding: 0,
            margin: 0,
        },
        infoItem: {
            display: 'flex',
            padding: '0.75rem 0',
            borderBottom: '1px solid #f3f4f6',
        },
        infoLabel: {
            width: '140px',
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#6b7280',
        },
        infoValue: {
            flex: 1,
            fontSize: '0.875rem',
            color: '#111827',
        },
        agencyCard: {
            padding: '1rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
        },
        agencyName: {
            fontSize: '1rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '0.5rem',
        },
        agencyDetails: {
            fontSize: '0.875rem',
            color: '#6b7280',
        },
        agencyDetail: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem',
            marginTop: '0.25rem',
        },
        documentsList: {
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '1rem',
        },
        documentCard: {
            padding: '1rem',
            backgroundColor: '#f9fafb',
            borderRadius: '0.5rem',
            border: '1px solid #e5e7eb',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        documentCardHover: {
            borderColor: '#2563eb',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
        },
        documentIcon: {
            fontSize: '2rem',
            marginBottom: '0.5rem',
        },
        documentName: {
            fontSize: '0.875rem',
            fontWeight: 500,
            color: '#111827',
            marginBottom: '0.25rem',
        },
        documentMeta: {
            fontSize: '0.75rem',
            color: '#6b7280',
        },
        amountPositive: {
            color: '#059669',
            fontWeight: 600,
        },
        amountNegative: {
            color: '#dc2626',
            fontWeight: 600,
        },
        statusModal: {
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
        },
        statusModalContent: {
            backgroundColor: '#ffffff',
            borderRadius: '0.5rem',
            padding: '2rem',
            maxWidth: '400px',
            width: '90%',
        },
        statusModalTitle: {
            fontSize: '1.25rem',
            fontWeight: 600,
            color: '#111827',
            marginBottom: '1.5rem',
        },
        statusOptions: {
            display: 'flex',
            flexDirection: 'column',
            gap: '0.75rem',
            marginBottom: '2rem',
        },
        statusOption: {
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
            padding: '0.75rem',
            border: '1px solid #e5e7eb',
            borderRadius: '0.375rem',
            cursor: 'pointer',
            transition: 'all 0.2s',
        },
        statusOptionSelected: {
            borderColor: '#2563eb',
            backgroundColor: '#eff6ff',
        },
        statusOptionRadio: {
            width: '1rem',
            height: '1rem',
            cursor: 'pointer',
        },
        modalButtons: {
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '1rem',
        },
        cancelButton: {
            padding: '0.5rem 1rem',
            backgroundColor: '#ffffff',
            color: '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
        },
        saveButton: {
            padding: '0.5rem 1rem',
            backgroundColor: '#2563eb',
            color: '#ffffff',
            border: 'none',
            borderRadius: '0.375rem',
            fontSize: '0.875rem',
            fontWeight: 500,
            cursor: 'pointer',
        },
        saveButtonDisabled: {
            backgroundColor: '#9ca3af',
            cursor: 'not-allowed',
        },
        emptyState: {
            textAlign: 'center',
            padding: '3rem 1.5rem',
            color: '#6b7280',
            fontSize: '0.875rem',
        },
    };

    useEffect(() => {
        if (id) {
            dispatch(fetchAttestation(id));
        }
    }, [dispatch, id]);

    useEffect(() => {
        if (selectedAttestation) {
            setNewStatus(selectedAttestation.status);
        }
    }, [selectedAttestation]);

    const getStatusStyle = (status) => {
        const option = statusOptions.find(s => s.value === status);
        return {
            backgroundColor: option?.bg || '#f3f4f6',
            color: option?.color || '#374151',
        };
    };

    const handleDelete = useCallback(async () => {
        try {
            await dispatch(deleteAttestation(id));
            navigate('/admin/attestations');
        } catch (error) {
            console.error('Delete failed:', error);
        }
    }, [dispatch, id, navigate]);

    const handleSync = useCallback(async () => {
        try {
            await dispatch(syncAttestation(id));
        } catch (error) {
            console.error('Sync failed:', error);
        }
    }, [dispatch, id]);

    const handleStatusUpdate = useCallback(async () => {
        try {
            await dispatch(updateAttestationStatus(id, newStatus));
            setShowStatusModal(false);
        } catch (error) {
            console.error('Status update failed:', error);
        }
    }, [dispatch, id, newStatus]);

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '—';
        return new Intl.NumberFormat('fr-FR', {
            style: 'currency',
            currency: 'XOF',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(amount);
    };

    const formatDate = (date) => {
        if (!date) return '—';
        return new Date(date).toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    const formatFileSize = (bytes) => {
        if (!bytes || bytes === 0) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    };

    const getFileIcon = (fileType) => {
        if (fileType?.startsWith('image/')) return '🖼️';
        if (fileType?.includes('pdf')) return '📄';
        if (fileType?.includes('word')) return '📝';
        if (fileType?.includes('excel')) return '📊';
        return '📎';
    };

    if (loading && !selectedAttestation) {
        return <LoadingSpinner fullScreen />;
    }

    if (!selectedAttestation && !loading) {
        return (
            <div style={styles.container}>
                <Alert
                    type="error"
                    message="Attestation non trouvée"
                />
                <Link to="/admin/attestations" style={styles.backButton}>
                    Retour à la liste
                </Link>
            </div>
        );
    }

    const tabs = [
        { id: 'info', label: 'Informations' },
        { id: 'agencies', label: 'Agences' },
        { id: 'documents', label: `Documents (${selectedAttestation.documents?.length || 0})` },
        { id: 'timeline', label: 'Chronologie' },
    ];

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <div style={styles.headerLeft}>
                    <button
                        onClick={() => navigate('/admin/attestations')}
                        style={styles.backButton}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = styles.backButtonHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = styles.backButton.backgroundColor;
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                        </svg>
                    </button>
                    <h1 style={styles.title}>Détails de l'attestation</h1>
                </div>
                <div style={styles.headerActions}>
                    <Link
                        to={`/admin/attestations/${id}/edit`}
                        style={{
                            ...styles.actionButton,
                            ...styles.editButton,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = styles.editButtonHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = styles.editButton.backgroundColor;
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                        </svg>
                        Modifier
                    </Link>
                    <button
                        onClick={handleSync}
                        disabled={syncProgress > 0 && syncProgress < 100}
                        style={{
                            ...styles.actionButton,
                            ...styles.syncButton,
                            ...(syncProgress > 0 && syncProgress < 100 ? { opacity: 0.7 } : {}),
                        }}
                        onMouseEnter={(e) => {
                            if (!(syncProgress > 0 && syncProgress < 100)) {
                                e.currentTarget.style.backgroundColor = styles.syncButtonHover.backgroundColor;
                            }
                        }}
                        onMouseLeave={(e) => {
                            if (!(syncProgress > 0 && syncProgress < 100)) {
                                e.currentTarget.style.backgroundColor = styles.syncButton.backgroundColor;
                            }
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        {syncProgress > 0 && syncProgress < 100 ? `Sync ${syncProgress}%` : 'Synchroniser'}
                    </button>
                    <button
                        onClick={() => setShowDocumentManager(true)}
                        style={{
                            ...styles.actionButton,
                            backgroundColor: '#10b981',
                            color: '#ffffff',
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#059669';
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#10b981';
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                        </svg>
                        Gérer les documents
                    </button>
                    <button
                        onClick={() => setShowStatusModal(true)}
                        style={{
                            ...styles.actionButton,
                            ...styles.statusButton,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = styles.statusButtonHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = styles.statusButton.backgroundColor;
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Changer le statut
                    </button>
                    <button
                        onClick={() => setShowDeleteConfirm(true)}
                        style={{
                            ...styles.actionButton,
                            ...styles.deleteButton,
                        }}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = styles.deleteButtonHover.backgroundColor;
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = styles.deleteButton.backgroundColor;
                        }}
                    >
                        <svg style={styles.icon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                        Supprimer
                    </button>
                </div>
            </div>

            {error && (
                <Alert
                    type="error"
                    message={error}
                    onClose={() => dispatch(clearAttestationError())}
                />
            )}

            <div style={styles.content}>
                {/* Hero Section */}
                <div style={styles.hero}>
                    <div style={styles.heroContent}>
                        <div style={styles.heroLeft}>
                            <div style={styles.attestationNumber}>
                                {selectedAttestation.numAttestation || 'Nouvelle attestation'}
                            </div>
                            <div style={styles.attestationMeta}>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}>Client</span>
                                    <span style={styles.metaValue}>
                                        {selectedAttestation.firstName} {selectedAttestation.lastName}
                                    </span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}>Police</span>
                                    <span style={styles.metaValue}>{selectedAttestation.numPolice || '—'}</span>
                                </div>
                                <div style={styles.metaItem}>
                                    <span style={styles.metaLabel}>Émission</span>
                                    <span style={styles.metaValue}>{selectedAttestation.numEmission || '—'}</span>
                                </div>
                            </div>
                        </div>
                        <div>
                            <div
                                style={{
                                    ...styles.badge,
                                    ...getStatusStyle(selectedAttestation.status),
                                }}
                            >
                                {statusOptions.find(s => s.value === selectedAttestation.status)?.label || selectedAttestation.status}
                            </div>
                            {selectedAttestation.synched && (
                                <div style={styles.syncBadge}>
                                    <span style={styles.syncSuccess}>✅ Synchronisé</span>
                                    {selectedAttestation.synchedAt && (
                                        <span style={{ fontSize: '0.75rem' }}>
                                            {new Date(selectedAttestation.synchedAt).toLocaleDateString()}
                                        </span>
                                    )}
                                </div>
                            )}
                            {syncProgress > 0 && syncProgress < 100 && (
                                <div style={styles.progressBar}>
                                    <div style={{ ...styles.progressFill, width: `${syncProgress}%` }} />
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Stats Cards */}
                    <div style={styles.statsGrid}>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Prime nette</div>
                            <div style={styles.statValue}>
                                {selectedAttestation.primeNet ? formatCurrency(selectedAttestation.primeNet) : '—'}
                            </div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Prime TTC</div>
                            <div style={{ ...styles.statValue, color: '#059669' }}>
                                {selectedAttestation.PrimeTTC ? formatCurrency(selectedAttestation.PrimeTTC) : '—'}
                            </div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Accessoires</div>
                            <div style={styles.statValue}>
                                {selectedAttestation.accessories ? formatCurrency(selectedAttestation.accessories) : '—'}
                            </div>
                        </div>
                        <div style={styles.statCard}>
                            <div style={styles.statLabel}>Documents</div>
                            <div style={styles.statValue}>{selectedAttestation.documents?.length || 0}</div>
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs
                    tabs={tabs}
                    activeTab={activeTab}
                    onTabChange={setActiveTab}
                />

                {/* Tab Content */}
                <div style={styles.tabContent}>
                    {activeTab === 'info' && (
                        <div style={styles.infoGrid}>
                            {/* Personal Information */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Informations personnelles</h3>
                                <ul style={styles.infoList}>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Civilité</span>
                                        <span style={styles.infoValue}>{selectedAttestation.title || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Nom complet</span>
                                        <span style={styles.infoValue}>
                                            {selectedAttestation.firstName} {selectedAttestation.middleName ? selectedAttestation.middleName + ' ' : ''}
                                            {selectedAttestation.lastName}
                                        </span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Date de naissance</span>
                                        <span style={styles.infoValue}>{formatDate(selectedAttestation.dateOfBirth)}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Genre</span>
                                        <span style={styles.infoValue}>{selectedAttestation.gender || '—'}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Identification Numbers */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Numéros d'identification</h3>
                                <ul style={styles.infoList}>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>N° Attestation</span>
                                        <span style={styles.infoValue}>{selectedAttestation.numAttestation || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>N° Police</span>
                                        <span style={styles.infoValue}>{selectedAttestation.numPolice || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>N° Émission</span>
                                        <span style={styles.infoValue}>{selectedAttestation.numEmission || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>N° Identité</span>
                                        <span style={styles.infoValue}>{selectedAttestation.idNumber || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Permis de conduire</span>
                                        <span style={styles.infoValue}>{selectedAttestation.drivingLicense || '—'}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>N° Assurance</span>
                                        <span style={styles.infoValue}>{selectedAttestation.insuranceNumber || '—'}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Dates */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Dates importantes</h3>
                                <ul style={styles.infoList}>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Date d'émission</span>
                                        <span style={styles.infoValue}>{formatDate(selectedAttestation.DateEmission)}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Date d'effet</span>
                                        <span style={styles.infoValue}>{formatDate(selectedAttestation.DateEffet)}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Date d'échéance</span>
                                        <span style={styles.infoValue}>{formatDate(selectedAttestation.DateEcheance)}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Financial Information */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Informations financières</h3>
                                <ul style={styles.infoList}>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Prime nette</span>
                                        <span style={styles.infoValue}>{formatCurrency(selectedAttestation.primeNet)}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Prime TTC</span>
                                        <span style={{ ...styles.infoValue, ...styles.amountPositive }}>
                                            {formatCurrency(selectedAttestation.PrimeTTC)}
                                        </span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Accessoires</span>
                                        <span style={styles.infoValue}>{formatCurrency(selectedAttestation.accessories)}</span>
                                    </li>
                                </ul>
                            </div>

                            {/* Metadata */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Métadonnées</h3>
                                <ul style={styles.infoList}>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Statut</span>
                                        <span style={styles.infoValue}>{selectedAttestation.status}</span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Créé le</span>
                                        <span style={styles.infoValue}>
                                            {new Date(selectedAttestation.createdAt).toLocaleString()}
                                        </span>
                                    </li>
                                    <li style={styles.infoItem}>
                                        <span style={styles.infoLabel}>Modifié le</span>
                                        <span style={styles.infoValue}>
                                            {new Date(selectedAttestation.updatedAt).toLocaleString()}
                                        </span>
                                    </li>
                                    {selectedAttestation.synchedAt && (
                                        <li style={styles.infoItem}>
                                            <span style={styles.infoLabel}>Synchronisé le</span>
                                            <span style={styles.infoValue}>
                                                {new Date(selectedAttestation.synchedAt).toLocaleString()}
                                            </span>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        </div>
                    )}

                    {activeTab === 'agencies' && (
                        <div style={styles.infoGrid}>
                            {/* Sender Agency */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Agence émettrice</h3>
                                {selectedAttestation.SenderAgency ? (
                                    <div style={styles.agencyCard}>
                                        <div style={styles.agencyName}>{selectedAttestation.SenderAgency.name}</div>
                                        <div style={styles.agencyDetails}>
                                            {selectedAttestation.SenderAgency.codeinte && (
                                                <div style={styles.agencyDetail}>
                                                    <span>🏢 Code: {selectedAttestation.SenderAgency.codeinte}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.SenderAgency.address && (
                                                <div style={styles.agencyDetail}>
                                                    <span>📍 {selectedAttestation.SenderAgency.address}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.SenderAgency.city && (
                                                <div style={styles.agencyDetail}>
                                                    <span>🏙️ {selectedAttestation.SenderAgency.city}</span>
                                                    {selectedAttestation.SenderAgency.zipCode && ` ${selectedAttestation.SenderAgency.zipCode}`}
                                                </div>
                                            )}
                                            {selectedAttestation.SenderAgency.phone && (
                                                <div style={styles.agencyDetail}>
                                                    <span>📞 {selectedAttestation.SenderAgency.phone}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.SenderAgency.email && (
                                                <div style={styles.agencyDetail}>
                                                    <span>✉️ {selectedAttestation.SenderAgency.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={styles.emptyState}>Aucune agence émettrice</div>
                                )}
                            </div>

                            {/* Receiver Agency */}
                            <div style={styles.infoSection}>
                                <h3 style={styles.sectionTitle}>Agence réceptrice</h3>
                                {selectedAttestation.ReceiverAgency ? (
                                    <div style={styles.agencyCard}>
                                        <div style={styles.agencyName}>{selectedAttestation.ReceiverAgency.name}</div>
                                        <div style={styles.agencyDetails}>
                                            {selectedAttestation.ReceiverAgency.codeinte && (
                                                <div style={styles.agencyDetail}>
                                                    <span>🏢 Code: {selectedAttestation.ReceiverAgency.codeinte}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.ReceiverAgency.address && (
                                                <div style={styles.agencyDetail}>
                                                    <span>📍 {selectedAttestation.ReceiverAgency.address}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.ReceiverAgency.city && (
                                                <div style={styles.agencyDetail}>
                                                    <span>🏙️ {selectedAttestation.ReceiverAgency.city}</span>
                                                    {selectedAttestation.ReceiverAgency.zipCode && ` ${selectedAttestation.ReceiverAgency.zipCode}`}
                                                </div>
                                            )}
                                            {selectedAttestation.ReceiverAgency.phone && (
                                                <div style={styles.agencyDetail}>
                                                    <span>📞 {selectedAttestation.ReceiverAgency.phone}</span>
                                                </div>
                                            )}
                                            {selectedAttestation.ReceiverAgency.email && (
                                                <div style={styles.agencyDetail}>
                                                    <span>✉️ {selectedAttestation.ReceiverAgency.email}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                ) : (
                                    <div style={styles.emptyState}>Aucune agence réceptrice</div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'documents' && (
                        <div>
                            {selectedAttestation.documents && selectedAttestation.documents.length > 0 ? (
                                <>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                        <h3 style={{ fontSize: '1rem', fontWeight: 600, color: '#111827', margin: 0 }}>
                                            Documents ({selectedAttestation.documents.length})
                                        </h3>
                                        <Link
                                            to={`/admin/attestations/${id}/documents`}
                                            style={{
                                                padding: '0.5rem 1rem',
                                                backgroundColor: '#2563eb',
                                                color: '#ffffff',
                                                border: 'none',
                                                borderRadius: '0.375rem',
                                                fontSize: '0.875rem',
                                                fontWeight: 500,
                                                textDecoration: 'none',
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                gap: '0.5rem',
                                            }}
                                        >
                                            <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                            </svg>
                                            Gérer tous les documents
                                        </Link>
                                    </div>
                                    <div style={styles.documentsList}>
                                        {selectedAttestation.documents.slice(0, 6).map((doc) => (
                                            <div
                                                key={doc.id}
                                                style={styles.documentCard}
                                                onMouseEnter={(e) => {
                                                    e.currentTarget.style.borderColor = styles.documentCardHover.borderColor;
                                                    e.currentTarget.style.boxShadow = styles.documentCardHover.boxShadow;
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.currentTarget.style.borderColor = '#e5e7eb';
                                                    e.currentTarget.style.boxShadow = 'none';
                                                }}
                                                onClick={() => window.open(doc.storagePath, '_blank')}
                                            >
                                                <div style={styles.documentIcon}>{getFileIcon(doc.fileType, doc.name)}</div>
                                                <div style={styles.documentName}>{doc.originalName || doc.name}</div>
                                                <div style={styles.documentMeta}>
                                                    {formatFileSize(doc.fileSize)}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {selectedAttestation.documents.length > 6 && (
                                        <div style={{ textAlign: 'center', marginTop: '1rem' }}>
                                            <Link
                                                to={`/admin/attestations/${id}/documents`}
                                                style={{ color: '#2563eb', textDecoration: 'none' }}
                                            >
                                                Voir tous les {selectedAttestation.documents.length} documents →
                                            </Link>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div style={styles.emptyState}>
                                    <svg style={{ width: '3rem', height: '3rem', marginBottom: '1rem', color: '#9ca3af' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
                                    </svg>
                                    <div style={{ marginBottom: '1rem' }}>
                                        Aucun document associé à cette attestation
                                    </div>
                                    <Link
                                        to={`/admin/attestations/${id}/documents`}
                                        style={{
                                            padding: '0.5rem 1rem',
                                            backgroundColor: '#2563eb',
                                            color: '#ffffff',
                                            border: 'none',
                                            borderRadius: '0.375rem',
                                            fontSize: '0.875rem',
                                            fontWeight: 500,
                                            textDecoration: 'none',
                                            display: 'inline-flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                        }}
                                    >
                                        <svg style={{ width: '1rem', height: '1rem' }} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                        </svg>
                                        Gérer les documents
                                    </Link>
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'timeline' && (
                        <div style={styles.emptyState}>
                            Chronologie des événements à venir
                        </div>
                    )}
                </div>
            </div>

            {/* Status Update Modal */}
            {showStatusModal && (
                <div style={styles.statusModal} onClick={() => setShowStatusModal(false)}>
                    <div style={styles.statusModalContent} onClick={(e) => e.stopPropagation()}>
                        <h3 style={styles.statusModalTitle}>
                            Changer le statut de l'attestation
                        </h3>
                        <div style={styles.statusOptions}>
                            {statusOptions.map((option) => (
                                <div
                                    key={option.value}
                                    style={{
                                        ...styles.statusOption,
                                        ...(newStatus === option.value ? styles.statusOptionSelected : {}),
                                    }}
                                    onClick={() => setNewStatus(option.value)}
                                >
                                    <input
                                        type="radio"
                                        name="status"
                                        value={option.value}
                                        checked={newStatus === option.value}
                                        onChange={() => setNewStatus(option.value)}
                                        style={styles.statusOptionRadio}
                                    />
                                    <span style={{ flex: 1 }}>{option.label}</span>
                                    <span
                                        style={{
                                            padding: '0.25rem 0.75rem',
                                            borderRadius: '9999px',
                                            backgroundColor: option.bg,
                                            color: option.color,
                                            fontSize: '0.75rem',
                                        }}
                                    >
                                        {option.label}
                                    </span>
                                </div>
                            ))}
                        </div>
                        <div style={styles.modalButtons}>
                            <button
                                onClick={() => setShowStatusModal(false)}
                                style={styles.cancelButton}
                            >
                                Annuler
                            </button>
                            <button
                                onClick={handleStatusUpdate}
                                disabled={newStatus === selectedAttestation.status}
                                style={{
                                    ...styles.saveButton,
                                    ...(newStatus === selectedAttestation.status ? styles.saveButtonDisabled : {}),
                                }}
                            >
                                Mettre à jour
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation */}
            <ConfirmDialog
                isOpen={showDeleteConfirm}
                onClose={() => setShowDeleteConfirm(false)}
                onConfirm={handleDelete}
                title="Supprimer l'attestation"
                message={`Êtes-vous sûr de vouloir supprimer l'attestation "${selectedAttestation?.numAttestation}" ?`}
                confirmText="Supprimer"
                cancelText="Annuler"
                type="danger"
            />
        </div>
    );
};

export default AttestationDetails;